import { ReactNode } from 'react';
import { useSelector } from 'react-redux';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { UserAccessLevel, UserRole } from '../../types/user';
import { APP_ROUTES } from '../../utils/constants/appRoutes';
import { parseAccessLevel } from '../../utils/helpers/organizationAccess';
import { getFullPath, REDIRECT_QUERY_PARAM } from '../../utils/redirectAfterLogin';
import Layout from '../Layout';

interface ProtectedRouteProps {
  authorizedUsers: UserRole[];
  children?: ReactNode;
}

const RolesNumber: Record<UserAccessLevel, UserRole> = {
  [UserAccessLevel.SUPER_ADMIN]: UserRole.SUPER_ADMIN,
  [UserAccessLevel.ORGANIZATION_ADMIN]: UserRole.ORGANIZATION_ADMIN,
  [UserAccessLevel.DEVICE_GROUP_ADMIN]: UserRole.DEVICE_GROUP_ADMIN,
  [UserAccessLevel.REGULAR_USER]: UserRole.REGULAR_USER,
  [UserAccessLevel.ORGANIZATION_VIEWER]: UserRole.ORGANIZATION_VIEWER,
};

export const ProtectedRoute = ({ authorizedUsers, children }: ProtectedRouteProps) => {
  const location = useLocation();
  const { user } = useSelector((state: any) => state.user);
  const isAuthenticated = !!user;

  const accessLevel = parseAccessLevel(user);
  const userRole = Number.isFinite(accessLevel)
    ? RolesNumber[accessLevel as UserAccessLevel]
    : undefined;
  const isUserAuthorized = userRole ? authorizedUsers.includes(userRole) : false;

  if (!isAuthenticated || !isUserAuthorized) {
    const fullPath = getFullPath(location);
    const searchParams = new URLSearchParams();
    if (fullPath) searchParams.set(REDIRECT_QUERY_PARAM, fullPath);
    const toAuth = searchParams.toString() ? `${APP_ROUTES.AUTH}?${searchParams}` : APP_ROUTES.AUTH;
    return <Navigate to={toAuth} replace state={{ from: location }} />;
  }

  return <Layout>{children ?? <Outlet />}</Layout>;
};
